import type { LanguageType } from '@/interface/enum';
import { BrandType, toStatistics } from '@/interface/enum';
import { isIOS } from '@/libraries/utils/userAgent';

const firstTLDs = 'ac|ad|ae|af|ag|ai|al|am|an|ao|aq|ar|as|at|au|aw|ax|az|ba|bb|be|bf|bg|bh|bi|bj|bm|bo|br|bs|bt|bv|bw|by|bz|ca|cc|cd|cf|cg|ch|ci|cl|cm|cn|co|cr|cu|cv|cw|cx|cz|de|dj|dk|dm|do|dz|ec|ee|eg|es|et|eu|fi|fm|fo|fr|ga|gb|gd|ge|gf|gg|gh|gi|gl|gm|gn|gp|gq|gr|gs|gt|gw|gy|hk|hm|hn|hr|ht|hu|id|ie|im|in|io|iq|ir|is|it|je|jo|jp|kg|ki|km|kn|kp|kr|ky|kz|la|lb|lc|li|lk|lr|ls|lt|lu|lv|ly|ma|mc|md|me|mg|mh|mk|ml|mn|mo|mp|mq|mr|ms|mt|mu|mv|mw|mx|my|na|nc|ne|nf|ng|nl|no|nr|nu|nz|om|pa|pe|pf|ph|pk|pl|pm|pn|pr|ps|pt|pw|py|qa|re|ro|rs|ru|rw|sa|sb|sc|sd|se|sg|sh|si|sj|sk|sl|sm|sn|so|sr|st|su|sv|sx|sy|sz|tc|td|tf|tg|th|tj|tk|tl|tm|tn|to|tp|tr|tt|tv|tw|tz|ua|ug|uk|us|uy|uz|va|vc|ve|vg|vi|vn|vu|wf|ws|yt'.split('|');
const secondTLDs = 'com|edu|gov|net|mil|org|nom|sch|caa|res|off|gob|int|tur|ip6|uri|urn|asn|act|nsw|qld|tas|vic|pro|biz|adm|adv|agr|arq|art|ato|bio|bmd|cim|cng|cnt|ecn|eco|emp|eng|esp|etc|eti|far|fnd|fot|fst|g12|ggf|imb|ind|inf|jor|jus|leg|lel|mat|med|mus|not|ntr|odo|ppg|psc|psi|qsl|rec|slg|srv|teo|tmp|trd|vet|zlg|web|ltd|sld|pol|fin|k12|lib|pri|aip|fie|eun|sci|prd|cci|pvt|mod|idv|rel|sex|gen|nic|abr|bas|cal|cam|emr|fvg|laz|lig|lom|mar|mol|pmn|pug|sar|sic|taa|tos|umb|vao|vda|ven|mie|北海道|和歌山|神奈川|鹿児島|ass|rep|tra|per|ngo|soc|grp|plc|its|air|and|bus|can|ddr|jfk|mad|nrw|nyc|ski|spy|tcm|ulm|usa|war|fhs|vgs|dep|eid|fet|fla|flå|gol|hof|hol|sel|vik|cri|iwi|ing|abo|fam|gok|gon|gop|gos|aid|atm|gsm|sos|elk|waw|est|aca|bar|cpa|jur|law|sec|plo|www|bir|cbg|jar|khv|msk|nov|nsk|ptz|rnd|spb|stv|tom|tsk|udm|vrn|cmw|kms|nkz|snz|pub|fhv|red|ens|nat|rns|rnu|bbs|tel|bel|kep|nhs|dni|fed|isa|nsn|gub|e12|tec|орг|обр|упр|alt|nis|jpn|mex|ath|iki|nid|gda|inc|info|asia'.split('|');

export function getDomain(url: string) {
    const parts = url.replace(/^www\./, '').split('.').slice(-3);

    if (parts.length === 3) {
        if (!((firstTLDs.includes(parts[1]) || secondTLDs.includes(parts[1])) && (firstTLDs.includes(parts[2]) || secondTLDs.includes(parts[2])))) {
            parts.shift();
        }
    }

    return parts.join('.');
}

export function getHost() {
    return getDomain(window.location.hostname);
}

export function getUrl(envUrl: string, protocol?: string) {
    protocol = protocol ?? window.location.protocol;
    return `${protocol}//${envUrl.replace('{host}', getHost())}`;
}

export function getPpUrl() {
    return getUrl(__PP__);
}

export function getPpApiUrl() {
    return getUrl(__PP_API__);
}

export function getSportsUrl() {
    return getUrl(__SPORTS__);
}

export function getSportsAssetsUrl() {
    return __SPORTS_CDN_URL__ ?? getSportsUrl();
}

export function getSportsBsiUrl() {
    return getUrl(__SPORTS_BSI__);
}

export function getSportsAsiUrl() {
    return getUrl(__SPORTS_ASI__);
}

export function getSportsApiUrl() {
    // ref: 12e8e59f0a81ddb94a93620a74b9db80035041db
    if (getHost() === 'sbobet.com') {
        return getUrl(__SPORTS_API__, 'https:');
    }

    return getUrl(__SPORTS_API__);
}

export function getSportsApiBsiUrl() {
    return getUrl(__SPORTS_API_BSI__);
}

export function getSportsApiAsiUrl() {
    return getUrl(__SPORTS_API_ASI__);
}

export function getBardApiUrl() {
    return getUrl(__BARD_API__);
}

export function getOddsApiUrl(url: string, forceHttp = false) {
    const protocol = __ENV_DEV__ || forceHttp ? 'http:' : 'https:';
    return getUrl(url, protocol);
}

export function getOddsApiWsUrl(url: string) {
    if (!url.trim()) {
        return '';
    }
    const protocol = __ENV_DEV__ ? 'ws:' : 'wss:';
    return getUrl(url, protocol);
}

export function getJackpotUrl() {
    return getUrl(__JACKPOT_URL__);
}

export function getGamesAsiUrl() {
    return getUrl(__GAMES_ASI__);
}

export function getFunkyWidgetUrl() {
    return getUrl(__FUNKY_URL__);
}

export function getMiniGameWidgetUrl() {
    return getUrl(__TALOS_URL__);
}

export function getLaojingUrl() {
    return getUrl(__LAOJING_URL__);
}

export function getCasinoWidgetUrl() {
    return getUrl(__CASINO_WIDGET_URL__);
}

export function getArthasUrl() {
    return getUrl(__ARTHAS_URL__);
}

export function getDepositUrl() {
    return `${getPpUrl()}/goto/payment`;
}

export function getBetGeniusScriptUrl(customerId: string, providerMatchId: string, containerId: string) {
    const isFullScreenEnabled = isIOS() ? 'false' : 'true';
    return `${__BET_GENIUS_SCRIPT__}?customerId=${customerId}&fixtureId=${providerMatchId}&containerId=${containerId}&autoplayEnabled=true&width=100%25&height=100%25&allowFullScreen=${isFullScreenEnabled}`;
}

export function getInfoCenterUrl(brandType: BrandType, isMainDomain: boolean) {
    const infoCenterUrls = [
        {
            brandType: BrandType.Sbobet,
            isMainDomain: true,
            url: 'help.sbobet.com',
        },
        {
            brandType: BrandType.Sbotop,
            isMainDomain: true,
            url: 'help.sbotop.com',
        },
        {
            brandType: BrandType.Sbobet,
            isMainDomain: false,
            url: 'sbobetinformation.com',
        },
        {
            brandType: BrandType.Sbotop,
            isMainDomain: false,
            url: 'sbotopinformation.com',
        },
    ];
    const infoCenterUrl = infoCenterUrls.find(x => x.brandType === brandType && x.isMainDomain === isMainDomain);
    return infoCenterUrl ? getUrl(infoCenterUrl.url) : '';
}

export function buildStatisticUrl(matchId: string | number, rawLang: string, brand: string) {
    const lang = toStatistics(rawLang as LanguageType);
    return __STATISTIC_URL__
        .replace('{brand}', brand)
        .replace('{lang}', lang)
        .replace('{matchId}', String(matchId));
}

const SPORTS_PROMOTION_PATH = '/promotions/sports-promotion';
const SCORE_PREDICTION_PROMOTION_PATH = '/promotions/epl-prediction?fromSite=CastorIcon';

/** __PROMOTION_URL__ is the promotion site only; each page adds its own path (Junelson, 2026-10-08, same as Kratos TCSP-8392). */
export function getSportsPromotionUrl() {
    return `${getUrl(__PROMOTION_URL__)}${SPORTS_PROMOTION_PATH}`;
}

/** B2C Event page of the Score Prediction promotion (TCSP-8390). */
export function getScorePredictionPromotionUrl(): string {
    return `${getUrl(__PROMOTION_URL__)}${SCORE_PREDICTION_PROMOTION_PATH}`;
}

export function setUrlParam(url: string, param: string, newVal: string | number) {
    const regex = new RegExp(`([?;&])${param}[^&;]*[;&]?`);
    const query = url.replace(regex, '$1').replace(/&$/, '');

    return `${query.length > 2 ? `${query}&` : '?'}${param}=${newVal}`;
}
